import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Mail, User } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EmailCaptureModal({
  open, onOpenChange, priceDisplay, childName, deityName,
  onSubmit, submitting,
}) {
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [touched, setTouched] = useState({ name: false, email: false });

  useEffect(() => {
    if (open) {
      setTouched({ name: false, email: false });
    }
  }, [open]);

  const nameError = touched.name && parentName.trim().length < 2 ? "Please enter your name" : '';
  const emailError = touched.email && !EMAIL_RE.test(parentEmail.trim()) ? "Please enter a valid email" : '';

  const canSubmit = parentName.trim().length >= 2 && EMAIL_RE.test(parentEmail.trim()) && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true });
    if (!canSubmit) return;
    await onSubmit({ parent_name: parentName.trim(), parent_email: parentEmail.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="email-capture-modal" className="sm:max-w-md bg-white">
        <DialogHeader>
          <div className="flex items-center gap-2 text-tl-purple">
            <Lock className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-[0.14em]">Almost there</span>
          </div>
          <DialogTitle className="text-[20px] font-semibold text-slate-900">
            Unlock {childName ? `${childName}'s` : "your child's"} full story
          </DialogTitle>
          <DialogDescription className="text-[13px] text-slate-600">
            We'll email you the complete story and a link to download the PDF right after payment.
            {deityName ? ` This story features ${deityName}.` : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          <div>
            <Label htmlFor="parent_name" className="text-[12px] text-slate-600">Your name</Label>
            <div className="relative mt-1.5">
              <User className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="parent_name"
                data-testid="email-modal-parent-name"
                value={parentName}
                onChange={(e) => setParentName(e.target.value.slice(0, 60))}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                placeholder="Your full name"
                className={`pl-9 h-11 ${nameError ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
                autoFocus
              />
            </div>
            {nameError && <p className="mt-1 text-[11px] text-red-500">{nameError}</p>}
          </div>

          <div>
            <Label htmlFor="parent_email" className="text-[12px] text-slate-600">Email address</Label>
            <div className="relative mt-1.5">
              <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="parent_email"
                data-testid="email-modal-parent-email"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value.slice(0, 120))}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                placeholder="you@example.com"
                className={`pl-9 h-11 ${emailError ? 'border-red-400 focus-visible:ring-red-300' : ''}`}
              />
            </div>
            {emailError && <p className="mt-1 text-[11px] text-red-500">{emailError}</p>}
            <p className="mt-1.5 text-[11px] text-slate-500">We'll send the full story here. No spam, ever.</p>
          </div>

          <Button
            type="submit"
            data-testid="email-modal-submit"
            disabled={!canSubmit}
            className="w-full h-12 bg-tl-purple hover:bg-tl-purpleDark text-white text-[13px] disabled:opacity-60"
          >
            {submitting ? 'Processing…' : `Continue to payment — ${priceDisplay}`}
          </Button>

          <p className="text-center text-[10px] text-slate-400">Payments processed securely by Dodo Payments</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
